'use client';

import {
	Checkbox, createSlotRecipeContext, Table,
	TableBody,
	TableCell,
	TableColumnHeader,
	TableHeader,
	TableRow,
} from '@chakra-ui/react';
import type {
	HTMLChakraProps,
	RecipeVariantProps,
	TableCellProps,
	TableRootProps,
} from '@chakra-ui/react';
import type { PropsWithChildren } from 'react';

import { selectTableSlotRecipe } from '../../theme';

import {
	SelectionContext,
	useSelectionContext,
} from './SelectTable.context';
import type { SelectionCheckboxProps } from './SelectTable.context';

const { withContext, withProvider } = createSlotRecipeContext({ recipe: selectTableSlotRecipe });

export type BaseRowProps = HTMLChakraProps<'tr'>;
const BaseRow = withContext<HTMLTableRowElement, BaseRowProps>(TableRow, 'row');

export type BaseHeaderProps = HTMLChakraProps<'thead'>;
const BaseHeader = withContext<HTMLTableSectionElement, BaseHeaderProps>(TableHeader, 'header');

type SelectTableVariantProps = RecipeVariantProps<typeof selectTableSlotRecipe>;
export type SelectTableRootProps = HTMLChakraProps<'table', SelectTableVariantProps>
	& TableRootProps
	& {
		headerSelection?: SelectionCheckboxProps
		selectable?:      boolean
		rowSelection?:    (id: number | string) => SelectionCheckboxProps
	};

function SelectTableRoot({
	children,
	headerSelection,
	rowSelection,
	selectable = false,
	...props
}: SelectTableRootProps) {
	const BaseRoot = withProvider<HTMLTableElement, SelectTableRootProps>(Table.Root, 'root');

	return (
		<SelectionContext.Provider value={{ headerSelection, rowSelection, selectable }}>
			<BaseRoot {...props} stickyHeader>
				{children}
			</BaseRoot>
		</SelectionContext.Provider>
	);
}

export type SelectTableCellProps = HTMLChakraProps<'td'> & TableCellProps;
const SelectTableCell = withContext<HTMLTableCellElement, SelectTableCellProps>(TableCell, 'cell');

export type SelectTableColHeaderProps = HTMLChakraProps<'th'>;
const SelectTableColumn = withContext<HTMLTableCellElement, SelectTableColHeaderProps>(TableColumnHeader, 'column');

export type SelectTableBodyProps = HTMLChakraProps<'tbody'>;
const SelectTableBody = withContext<HTMLTableSectionElement, SelectTableBodyProps>(TableBody, 'body');

function SelectionCheckbox({ checked, onCheckedChange }: SelectionCheckboxProps) {
	return (
		<Checkbox.Root checked={checked} onCheckedChange={onCheckedChange}>
			<Checkbox.HiddenInput />
			<Checkbox.Control />
		</Checkbox.Root>
	);
}

export type SelectTableHeaderProps = BaseHeaderProps;

function SelectTableHeader({ children, ...props }: SelectTableHeaderProps) {
	const context = useSelectionContext();
	const hasSelection = context?.selectable && !!context?.headerSelection;

	return (
		<BaseHeader {...props}>
			<BaseRow>
				{hasSelection && (
					<SelectTableColumn minW={0} pr={0} width="1%">
						<SelectionCheckbox
							checked={context.headerSelection!.checked}
							onCheckedChange={context.headerSelection!.onCheckedChange}
						/>
					</SelectTableColumn>
				)}
				{children}
			</BaseRow>
		</BaseHeader>
	);
}

export type SelectTableRowProps = {
	rowId: number | string
} & BaseRowProps;

export function SelectTableRow({ rowId, children, ...other }: SelectTableRowProps) {
	const context = useSelectionContext();
	const hasSelection = context?.selectable && !!context?.rowSelection;
	const selectionProps = context?.rowSelection?.(rowId);

	return (
		<BaseRow {...other}>
			{hasSelection && selectionProps && (
				<SelectTableCell pr={0} width="25px">
					<SelectionCheckbox
						checked={selectionProps.checked}
						onCheckedChange={selectionProps.onCheckedChange}
					/>
				</SelectTableCell>
			)}
			{children}
		</BaseRow>
	);
}

const ScrollArea = ({ children }: PropsWithChildren) => {
	return (
		<Table.ScrollArea borderWidth="1px" rounded="lg" scrollbar="hidden">
			{children}
		</Table.ScrollArea>
	);
};

export const SelectTable = {
	Body:       SelectTableBody,
	Cell:       SelectTableCell,
	Column:     SelectTableColumn,
	Header:     SelectTableHeader,
	Root:       SelectTableRoot,
	Row:        SelectTableRow,
	ScrollArea: ScrollArea,
};
